import { lazy, type ReactNode, Suspense, useCallback, useEffect, useState } from "react";
import { AppBar } from "./AppBar.tsx";
import { type AuthProviders, api, type Profile, type SessionResponse } from "./api.ts";
import { Home } from "./Home.tsx";
import { messages } from "./i18n.ts";
import { Module } from "./Module.tsx";
import { Profile as ProfilePage } from "./Profile.tsx";
import { SignIn } from "./SignIn.tsx";

// Lazily loaded, so the learner bundle does not contain admin code at all.
const Admin = lazy(() => import("./Admin.tsx").then((m) => ({ default: m.Admin })));

/**
 * A handful of screens, so routing is the History API rather than a router.
 * TanStack Router arrives when there is a tree worth routing.
 */
type Route =
  | { name: "home" }
  | { name: "module"; obligationId: string }
  | { name: "profile" }
  | { name: "admin" };

function readRoute(): Route {
  const path = window.location.pathname;
  const match = /^\/training\/([0-9a-f-]{36})$/.exec(path);
  if (match?.[1]) {
    return { name: "module", obligationId: match[1] };
  }
  if (path === "/admin") {
    return { name: "admin" };
  }
  if (path === "/profile") {
    return { name: "profile" };
  }
  return { name: "home" };
}

function useRoute(): [Route, (path: string) => void] {
  const [route, setRoute] = useState<Route>(readRoute);

  useEffect(() => {
    const onPop = () => setRoute(readRoute());
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((path: string) => {
    window.history.pushState({}, "", path);
    setRoute(readRoute());
  }, []);

  return [route, navigate];
}

export function App() {
  const [session, setSession] = useState<SessionResponse | null>(null);
  // Null once `providersUnavailable` is set means the list could not be
  // fetched, which is not the same as knowing nothing is configured. Answering
  // a refused fetch with `{ google: false, devLogin: false }` made a transient
  // rate limit read as a permanent configuration fault.
  const [providers, setProviders] = useState<AuthProviders | null>(null);
  const [providersUnavailable, setProvidersUnavailable] = useState(false);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [profileError, setProfileError] = useState(false);
  const [route, navigate] = useRoute();

  const refresh = useCallback(() => {
    api
      .session()
      .then(setSession)
      .catch(() => setSession({ authenticated: false }));
  }, []);

  const loadProfile = useCallback(() => {
    setProfileError(false);
    api
      .profile()
      .then(setProfile)
      .catch(() => setProfileError(true));
  }, []);

  const loadProviders = useCallback(() => {
    setProvidersUnavailable(false);
    api
      .providers()
      .then((result) => {
        setProviders(result);
      })
      .catch(() => {
        setProviders(null);
        setProvidersUnavailable(true);
      });
  }, []);

  useEffect(() => {
    refresh();
    loadProviders();
  }, [refresh, loadProviders]);

  useEffect(() => {
    if (session?.authenticated) {
      setProfile(null);
      loadProfile();
    } else {
      setProfile(null);
      setProfileError(false);
    }
  }, [session, loadProfile]);

  if (!session || (!providers && !providersUnavailable)) {
    return (
      <main className="flex min-h-dvh items-center justify-center">
        <p className="text-muted-foreground">{messages.common.loading}</p>
      </main>
    );
  }

  if (!session.authenticated) {
    return frame(
      <SignIn providers={providers} onRetryProviders={loadProviders} onSignedIn={refresh} />,
      providers,
    );
  }

  const user = session.user;
  const signOut = async () => {
    await api.signOut();
    navigate("/");
    refresh();
  };

  // The bar is the same on every authenticated screen. Roles come from the
  // profile when it has loaded; until then the administration group is simply
  // not offered, which is the safe direction to be wrong in.
  const shell = (content: ReactNode): ReactNode =>
    frame(
      <div className="flex min-h-dvh min-w-0 flex-col overflow-x-hidden bg-background">
        <AppBar
          email={user.email}
          name={user.name}
          onNavigate={navigate}
          onSignOut={signOut}
          orgName={user.orgName}
          orgSlug={user.orgSlug}
          roles={profile?.roles ?? []}
        />
        {content}
      </div>,
      providers,
    );

  // The module itself has no date-sensitive UI, so a temporary profile error
  // must not stop somebody reading training they already opened.
  if (route.name === "module") {
    return shell(
      <Module
        obligationId={route.obligationId}
        onBack={() => navigate("/")}
        timezone={profile?.timezone ?? null}
      />,
    );
  }

  if (!profile) {
    return shell(
      <main className="flex flex-1 flex-col items-center justify-center gap-3 p-4 text-center">
        <p className={profileError ? "text-destructive" : "text-muted-foreground"}>
          {profileError ? messages.app.profileLoadError : messages.common.loading}
        </p>
        {profileError ? (
          <button className="btn" type="button" onClick={loadProfile}>
            {messages.app.retryProfile}
          </button>
        ) : null}
      </main>,
    );
  }

  if (route.name === "profile") {
    return shell(
      <ProfilePage
        authProvider={user.authProvider}
        onBack={() => navigate("/")}
        onTimezoneChanged={(timezone) =>
          setProfile((current) => current && { ...current, timezone })
        }
        profile={profile}
      />,
    );
  }

  if (route.name === "admin") {
    return shell(
      <Suspense
        fallback={
          <main className="flex flex-1 items-center justify-center">
            <p className="text-muted-foreground">{messages.common.loading}</p>
          </main>
        }
      >
        <Admin orgName={user.orgName} timezone={profile.timezone} onBack={() => navigate("/")} />
      </Suspense>,
    );
  }

  return shell(
    <Home onOpen={(id) => navigate(`/training/${id}`)} timezone={profile.timezone} user={user} />,
  );
}

function frame(content: ReactNode, providers: AuthProviders | null): ReactNode {
  // No banner when the environment is unknown. Claiming a preview we have not
  // been told about would be the same mistake in the other direction.
  if (providers?.environment !== "preview") {
    return content;
  }
  return (
    <div className="min-w-0 bg-background">
      <div
        className="sticky top-0 z-50 border-b border-status-overdue-fg bg-status-overdue-bg px-4 py-2 text-center text-sm text-status-overdue-fg shadow-sm"
        role="status"
      >
        <strong>{messages.preview.title}.</strong> {messages.preview.warning}
      </div>
      {content}
    </div>
  );
}
